import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import {
  Circle,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import {
  FiBox,
  FiCompass,
  FiGlobe,
  FiLayers,
  FiMap,
  FiMaximize2,
  FiMinimize2,
  FiNavigation,
  FiTriangle,
} from 'react-icons/fi';
import { FaGoogle } from 'react-icons/fa';
import 'leaflet/dist/leaflet.css';
import './EmployeeMap.css';
import EmployeeMapAdvanced from './EmployeeMapAdvanced';
import EmployeeMapGoogle from './EmployeeMapGoogle';
import MapPlaceSearch from './MapPlaceSearch';
import { DEFAULT_MAP_VIEW, MAP_PROVIDERS, hasGoogleMapsKey } from './mapProviders';
import { resolveAvatarUrl, getOnlineStatus } from '../../utils/locationUtils';
import EmployeeMarkerDetails from './EmployeeMarkerDetails';
import LocationName from './LocationName';

const VIEW_ICONS = {
  google: FaGoogle,
  osm: FiMap,
  carto: FiCompass,
  topo: FiTriangle,
  esri: FiNavigation,
  satellite: FiGlobe,
  hybrid: FiLayers,
  '3d': FiBox,
};

/** Visible toggle order (skip alias keys like street/light). */
const MAP_VIEW_ORDER = ['google', 'osm', 'carto', 'topo', 'esri', 'satellite', 'hybrid', '3d'];

const MAP_VIEWS = Object.fromEntries(
  MAP_VIEW_ORDER.filter((key) => MAP_PROVIDERS[key]).map((key) => {
    const provider = MAP_PROVIDERS[key];
    return [
      key,
      {
        ...provider,
        icon: VIEW_ICONS[key] || FiMap,
        advanced: provider.engine === 'maplibre' || provider.engine === 'google',
      },
    ];
  })
);

const buildBoundsKey = (employees, offices) => {
  const empIds = employees
    .map((e) => e.userId)
    .filter(Boolean)
    .sort()
    .join(',');
  const officeIds = offices
    .map((o) => o.name)
    .filter(Boolean)
    .sort()
    .join(',');
  return `${empIds}|${officeIds}`;
};

const ADVANCED_MODES = new Set(['3d']);
const GOOGLE_MODES = new Set(['google']);
const STATUS_DOT_COLOR = {
  moving: '#3b82f6',
  'in-office': '#14b8a6',
  field: '#f59e0b',
  'checked-out': '#94a3b8',
  offline: '#fb7185',
  'no-location': '#cbd5e1',
};

const escapeHtml = (value = '') =>
  String(value).replace(/[&<>"']/g, (char) => {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return map[char] || char;
  });

const createProfileIcon = (employee, isSelected) => {
  const avatar = resolveAvatarUrl(employee.profilePic, employee.name);
  const isOnline = getOnlineStatus(employee).isOnline;
  const dotColor = isOnline
    ? '#22c55e'
    : STATUS_DOT_COLOR[employee.status] || STATUS_DOT_COLOR.offline;
  const pulseClass = isOnline ? 'employee-marker-pulse-online' : employee.status === 'moving' ? 'employee-marker-pulse' : '';
  const selectedClass = isSelected ? 'employee-marker-selected' : '';
  const approxClass = employee.isApproximateLocation ? 'employee-marker-approximate' : '';

  return L.divIcon({
    className: 'employee-map-marker-wrap',
    html: `
      <div class="employee-map-marker ${pulseClass} ${selectedClass} ${approxClass}">
        <img src="${escapeHtml(avatar)}" alt="${escapeHtml(employee.name)}" />
        <span class="employee-map-marker-dot" style="background:${dotColor}"></span>
      </div>
    `,
    iconSize: [48, 52],
    iconAnchor: [24, 52],
    popupAnchor: [0, -48],
  });
};

const createOfficeIcon = () =>
  L.divIcon({
    className: 'office-map-marker-wrap',
    html: '<div class="office-map-marker"></div>',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

const createSearchPinIcon = () =>
  L.divIcon({
    className: 'search-map-marker-wrap',
    html: '<div class="search-map-marker"></div>',
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });

function MapSearchFlyTo({ place, programmaticRef }) {
  const map = useMap();

  useEffect(() => {
    if (place?.lat == null || place?.lng == null) return;
    programmaticRef.current = true;
    map.flyTo([place.lat, place.lng], Math.max(map.getZoom(), 15), { duration: 1.2 });
    map.once('moveend', () => {
      programmaticRef.current = false;
    });
  }, [map, place?.id, place?.lat, place?.lng, programmaticRef]);

  return null;
}

/** Lock camera after the user zooms/pans so live GPS polls cannot snap zoom out. */
function MapUserInteractionLock({ programmaticRef, userLockedRef }) {
  useMapEvents({
    dragstart() {
      if (!programmaticRef.current) userLockedRef.current = true;
    },
    zoomstart() {
      if (!programmaticRef.current) userLockedRef.current = true;
    },
  });
  return null;
}

function MapBounds({
  employees,
  offices,
  center,
  zoom,
  selectedId,
  programmaticRef,
  userLockedRef,
  fitRequestToken = 0,
}) {
  const map = useMap();
  const lastBoundsKeyRef = useRef('');
  const lastFitTokenRef = useRef(0);
  const boundsKey = buildBoundsKey(employees, offices);

  useEffect(() => {
    const forceFit = fitRequestToken > 0 && fitRequestToken !== lastFitTokenRef.current;
    if (forceFit) {
      lastFitTokenRef.current = fitRequestToken;
      userLockedRef.current = false;
      lastBoundsKeyRef.current = '';
    }

    // While a person is selected (and not an explicit Fit all), FocusSelectedMarker owns the camera.
    if (selectedId && !forceFit) return;

    const alreadyFittedThisSet = lastBoundsKeyRef.current === boundsKey && boundsKey !== '';
    if (alreadyFittedThisSet && !forceFit) {
      return;
    }

    userLockedRef.current = false;

    const points = [];
    employees.forEach((emp) => {
      if (emp.latitude != null && emp.longitude != null) {
        points.push([emp.latitude, emp.longitude]);
      }
    });
    offices.forEach((office) => {
      points.push([office.latitude, office.longitude]);
    });

    programmaticRef.current = true;
    if (points.length === 1) {
      map.setView(points[0], 18);
    } else if (points.length > 1) {
      map.fitBounds(points, { padding: [60, 60], maxZoom: 18 });
    } else if (center) {
      map.setView([center.lat, center.lng], zoom);
    }
    lastBoundsKeyRef.current = boundsKey;
    map.once('moveend', () => {
      programmaticRef.current = false;
    });
  }, [
    map,
    employees,
    offices,
    center,
    zoom,
    selectedId,
    boundsKey,
    programmaticRef,
    userLockedRef,
    fitRequestToken,
  ]);

  return null;
}

function FocusSelectedMarker({ selectedEmployee, programmaticRef }) {
  const map = useMap();
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    if (selectedEmployee?.latitude == null || selectedEmployee?.longitude == null) {
      lastUserIdRef.current = null;
      return undefined;
    }

    const { userId, latitude, longitude } = selectedEmployee;
    const isNewSelection = lastUserIdRef.current !== userId;
    lastUserIdRef.current = userId;

    // Only jump/zoom when the user picks a different person.
    // Live GPS updates must not force zoom back out/in.
    if (!isNewSelection) return undefined;

    programmaticRef.current = true;
    const targetZoom = Math.max(map.getZoom(), 18);
    map.setView([latitude, longitude], targetZoom, { animate: true });
    map.once('moveend', () => {
      programmaticRef.current = false;
    });
    return undefined;
  }, [
    map,
    selectedEmployee?.userId,
    selectedEmployee?.latitude,
    selectedEmployee?.longitude,
    programmaticRef,
  ]);

  return null;
}

function MapResize({ resizeToken }) {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 120);
    return () => clearTimeout(timer);
  }, [map, resizeToken]);

  return null;
}

function MapViewToggle({ mapView, onChange, isFullView, onToggleFullView, onFitAll }) {
  return (
    <div className="employee-map-controls">
      <div className="employee-map-view-toggle">
        {Object.entries(MAP_VIEWS).map(([key, view]) => {
          const Icon = view.icon;
          const active = mapView === key;
          const needsKey = view.requiresKey && !hasGoogleMapsKey;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              className={`${active ? 'is-active' : ''} ${view.advanced ? 'view-advanced' : ''} ${key === 'google' ? 'view-google' : ''}`}
              title={
                needsKey
                  ? 'Add REACT_APP_GOOGLE_MAPS_API_KEY to enable Google Maps'
                  : `${view.label} view`
              }
            >
              <Icon />
              <span>{view.label}</span>
            </button>
          );
        })}
      </div>

      <div className="employee-map-control-row">
        {onFitAll ? (
          <button
            type="button"
            className="employee-map-fullscreen-btn"
            onClick={onFitAll}
            title="Fit all locations"
          >
            <FiMaximize2 />
            <span>Fit all</span>
          </button>
        ) : null}

        {onToggleFullView ? (
          <button
            type="button"
            className="employee-map-fullscreen-btn"
            onClick={onToggleFullView}
            title={isFullView ? 'Exit full view' : 'Full view map'}
          >
            {isFullView ? <FiMinimize2 /> : <FiMaximize2 />}
            <span>{isFullView ? 'Exit' : 'Full view'}</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

function EmployeeMap({
  employees = [],
  offices = [],
  selectedId,
  onSelectEmployee,
  center = { lat: 12.9716, lng: 80.2202 },
  zoom = 12,
  fullView = false,
  resizeToken = 0,
  isFullView = false,
  onToggleFullView,
}) {
  const [mapView, setMapView] = useState(DEFAULT_MAP_VIEW);
  const [searchPlace, setSearchPlace] = useState(null);
  const [tileUrlOverride, setTileUrlOverride] = useState({});
  const [fitRequestToken, setFitRequestToken] = useState(0);
  const programmaticRef = useRef(false);
  const userLockedRef = useRef(false);
  const activeLayer = MAP_VIEWS[mapView] || MAP_VIEWS[DEFAULT_MAP_VIEW];
  const baseTileUrl = tileUrlOverride[mapView] || activeLayer.url;

  const locatedEmployees = useMemo(
    () => employees.filter((e) => e.latitude != null && e.longitude != null),
    [employees]
  );

  const selectedEmployee = useMemo(
    () => locatedEmployees.find((e) => e.userId === selectedId) || null,
    [locatedEmployees, selectedId]
  );

  const trailPath = useMemo(() => {
    if (!selectedEmployee?.trail?.length) return [];
    return selectedEmployee.trail.map((p) => [p.lat, p.lng]);
  }, [selectedEmployee]);

  const isAdvancedView = ADVANCED_MODES.has(mapView);
  const isGoogleView = GOOGLE_MODES.has(mapView);

  const handleTileError = () => {
    if (!activeLayer.fallbackUrl) return;
    setTileUrlOverride((prev) => {
      if (prev[mapView] === activeLayer.fallbackUrl) return prev;
      return { ...prev, [mapView]: activeLayer.fallbackUrl };
    });
  };

  return (
    <div className={`employee-map-shell ${fullView ? 'is-fullview' : ''}`}>
      <MapPlaceSearch onSelectPlace={setSearchPlace} />

      <MapViewToggle
        mapView={mapView}
        onChange={setMapView}
        isFullView={isFullView}
        onToggleFullView={onToggleFullView}
        onFitAll={() => setFitRequestToken((token) => token + 1)}
      />

      {isGoogleView ? (
        <EmployeeMapGoogle
          employees={employees}
          offices={offices}
          selectedId={selectedId}
          onSelectEmployee={onSelectEmployee}
          center={center}
          zoom={zoom}
          resizeToken={resizeToken}
          fullView={fullView}
          searchPlace={searchPlace}
          fitRequestToken={fitRequestToken}
        />
      ) : isAdvancedView ? (
        <EmployeeMapAdvanced
          employees={employees}
          offices={offices}
          selectedId={selectedId}
          onSelectEmployee={onSelectEmployee}
          center={center}
          resizeToken={resizeToken}
          fullView={fullView}
          searchPlace={searchPlace}
          fitRequestToken={fitRequestToken}
        />
      ) : (
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={zoom}
        className="employee-leaflet-map"
        scrollWheelZoom
        maxZoom={activeLayer.maxZoom || 22}
      >
        <MapResize resizeToken={resizeToken} />
        <MapUserInteractionLock
          programmaticRef={programmaticRef}
          userLockedRef={userLockedRef}
        />
        <TileLayer
          key={`base-${mapView}-${baseTileUrl}`}
          attribution={activeLayer.attribution}
          url={baseTileUrl}
          maxZoom={activeLayer.maxZoom || 22}
          maxNativeZoom={activeLayer.maxNativeZoom || 19}
          eventHandlers={{
            tileerror: handleTileError,
          }}
        />

        {activeLayer.overlayUrl ? (
          <TileLayer
            key={`overlay-${mapView}`}
            url={activeLayer.overlayUrl}
            maxZoom={activeLayer.maxZoom || 22}
            maxNativeZoom={activeLayer.maxNativeZoom || 19}
            opacity={0.85}
          />
        ) : null}

      <MapBounds
        employees={locatedEmployees}
        offices={offices}
        center={center}
        zoom={zoom}
        selectedId={selectedId}
        programmaticRef={programmaticRef}
        userLockedRef={userLockedRef}
        fitRequestToken={fitRequestToken}
      />
      <FocusSelectedMarker
        selectedEmployee={selectedEmployee}
        programmaticRef={programmaticRef}
      />
      <MapSearchFlyTo place={searchPlace} programmaticRef={programmaticRef} />

      {searchPlace?.lat != null && searchPlace?.lng != null ? (
        <Marker position={[searchPlace.lat, searchPlace.lng]} icon={createSearchPinIcon()}>
          <Popup>
            <div className="text-sm min-w-[160px]">
              <p className="font-bold text-[#1a365d]">{searchPlace.shortLabel || 'Selected place'}</p>
              <p className="text-xs text-slate-500 mt-1">{searchPlace.label}</p>
            </div>
          </Popup>
        </Marker>
      ) : null}

      {offices.map((office) => (
        <React.Fragment key={office.name}>
          <Marker
            position={[office.latitude, office.longitude]}
            icon={createOfficeIcon()}
          >
            <Popup>
              <div className="text-sm">
                <p className="font-bold text-[#1a365d]">{office.name}</p>
                <p className="text-xs text-slate-500">Office geofence</p>
              </div>
            </Popup>
          </Marker>
          <Circle
            center={[office.latitude, office.longitude]}
            radius={office.radiusMeters || 400}
            pathOptions={{
              color: '#0d9488',
              fillColor: '#14b8a6',
              fillOpacity: 0.08,
              weight: 1,
            }}
          />
        </React.Fragment>
      ))}

      {trailPath.length > 1 && (
        <Polyline
          positions={trailPath}
          pathOptions={{
            color: '#1a365d',
            weight: 4,
            opacity: 0.85,
            dashArray: '8 6',
          }}
        />
      )}

      {locatedEmployees.map((employee) => {
        const isSelected = employee.userId === selectedId;

        return (
          <Marker
            key={employee.userId}
            position={[employee.latitude, employee.longitude]}
            icon={createProfileIcon(employee, isSelected)}
            eventHandlers={{
              click: () => onSelectEmployee?.(isSelected ? null : employee.userId),
            }}
          >
            <Tooltip
              direction="top"
              offset={[0, -52]}
              opacity={1}
              className="employee-marker-tooltip"
            >
              <span className="employee-marker-tooltip__name">{employee.name}</span>
              <span className="employee-marker-tooltip__role">
                {getOnlineStatus(employee).label}
                {employee.position ? ` · ${employee.position}` : ''}
              </span>
              <span className="employee-marker-tooltip__role">
                <LocationName
                  lat={employee.latitude}
                  lng={employee.longitude}
                  approximate={employee.isApproximateLocation}
                  accuracy={employee.accuracy}
                  compact
                  showCoords={false}
                />
              </span>
            </Tooltip>
            <Popup autoClose={false} closeOnClick={false} minWidth={240}>
              <EmployeeMarkerDetails employee={employee} />
            </Popup>
          </Marker>
        );
      })}
      </MapContainer>
      )}
    </div>
  );
}

export default EmployeeMap;
